import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWaiterStore } from '../store/waiterStore';
import { PaymentConfirmModal } from '../components/PaymentConfirmModal';

const statusMap: Record<string, { label: string; color: string; next?: string; nextLabel?: string }> = {
  pending: { label: 'Ожидает', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30', next: 'cooking', nextLabel: 'Готовится' },
  cooking: { label: 'Готовится', color: 'text-orange-400 bg-orange-400/10 border-orange-400/30', next: 'ready', nextLabel: 'Готов' },
  ready: { label: 'Готов', color: 'text-blue-400 bg-blue-400/10 border-blue-400/30', next: 'served', nextLabel: 'Подан' },
  served: { label: 'Подан', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30' },
  paid: { label: 'Оплачен', color: 'text-slate-400 bg-slate-400/10 border-slate-400/30' },
};

export const OrderDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { orders, updateOrderStatus, setOrderPayment } = useWaiterStore();
  const [showPayment, setShowPayment] = useState(false);

  const order = orders.find(o => o.id === orderId);

  if (!order) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400">Заказ не найден</p>
        <button
          onClick={() => navigate('/waiter')}
          className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-xl"
        >
          На главную
        </button>
      </div>
    );
  }

  const status = statusMap[order.status] || statusMap.pending;
  const createdTime = new Date(order.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

  const handleAdvanceStatus = () => {
    if (status.next) {
      updateOrderStatus(order.id, status.next as any);
    }
  };

  const handlePayment = (method: 'cash' | 'card') => {
    setOrderPayment(order.id, method);
    setShowPayment(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/waiter')}
          className="flex items-center text-slate-400 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Заказы
        </button>
        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${status.color}`}>
          {status.label}
        </span>
      </div>

      {/* Order header */}
      <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-white font-bold text-lg">Стол {order.tableNumber}</p>
            <p className="text-slate-400 text-sm">{order.client.name}</p>
          </div>
          <div className="text-right">
            <p className="text-slate-400 text-sm">{createdTime}</p>
            {order.rkeeperOrderId && (
              <p className="text-slate-500 text-xs">R-Keeper: {order.rkeeperOrderId}</p>
            )}
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 divide-y divide-slate-700/50">
        <div className="p-3">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Блюда</h3>
        </div>
        {order.items.map((item, i) => (
          <div key={i} className="p-3 flex items-center justify-between">
            <div className="flex-1">
              <p className="text-white text-sm">{item.name}</p>
              <p className="text-slate-400 text-xs">{item.quantity} x {item.price - (item.discount || 0)} ₽</p>
            </div>
            <p className="text-white text-sm font-medium">
              {(item.price - (item.discount || 0)) * item.quantity} ₽
            </p>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 p-4 space-y-2">
        {order.bonusUsed > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Бонусы</span>
            <span className="text-emerald-400">-{order.bonusUsed} ₽</span>
          </div>
        )}
        <div className="flex justify-between text-lg font-bold">
          <span className="text-white">Итого</span>
          <span className="text-emerald-400">{order.total} ₽</span>
        </div>
        {order.paymentMethod && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Оплата</span>
            <span className="text-white">{order.paymentMethod === 'cash' ? 'Наличные' : 'Карта'}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      {order.status !== 'paid' && (
        <div className="space-y-3">
          {status.next && (
            <button
              onClick={handleAdvanceStatus}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
            >
              {status.next === 'cooking' && 'Начать готовку'}
              {status.next === 'ready' && 'Заказ готов'}
              {status.next === 'served' && 'Подан гостю'}
            </button>
          )}

          {order.status === 'served' && (
            <button
              onClick={() => setShowPayment(true)}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-colors"
            >
              Принять оплату
            </button>
          )}
        </div>
      )}

      {/* Payment modal */}
      {showPayment && (
        <PaymentConfirmModal
          total={order.total}
          onConfirm={handlePayment}
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  );
};
